<template>
	<div class="cassie-vertical-md mt-1">
		<FilterBar
			:search-query.sync="actionLinkSearchQuery"
			search-query-label="Action Link Name"
			:selected-brand-id.sync="selectedAdminPortalBrandId"
			@persistSearchQuery="changeActionLinkSearchQuery"
			@persistSelectedBrandId="changeAdminPortalBrandFilter"
			@clearFilters="clearFilters"
		>
			<template #after-filters>
				<Dropdown
					v-model="linkAuthType"
					:items="authTypeFilter"
					custom-sort
					label="Authentication Type"
					@input="changeLinkAuthType($event)"
				/>
				<Dropdown
					v-model="linkEnabledStatus"
					:items="statusFilterItems"
					custom-sort
					label="Status"
					@input="changeLinkEnabledStatus($event)"
				/>
			</template>
			<template #action>
				<PrimaryActionButton
					v-if="userFullPermissions"
					@click="$emit('create-link', ACTION_LINK_TYPE)"
				>
					<v-icon left>
						mdi-plus
					</v-icon>
					Create
				</PrimaryActionButton>
			</template>
		</FilterBar>
		<SectionCard>
			<template #title>
				Action Links
			</template>
			<template #body>
				<DataTable
					:items="filteredActionLinks"
					:headers="tableHeaders"
					sort-by="linkId"
					sort-desc
					@click:row="rowClick"
				>
					<template #item.linkName="{ item }">
						{{ item.linkName }}
						<v-tooltip
							v-if="item.enableOneClickUnsubscribe"
							bottom
						>
							<template #activator="{ on, attrs }">
								<v-icon
									small
									class="ml-2"
									v-bind="attrs"
									v-on="on"
								>
									mdi-email-remove
								</v-icon>
							</template>
							One-Click Unsubscribe
						</v-tooltip>
					</template>
					<template #item.isEnabled="{ item }">
						{{ item.isEnabled ? 'Enabled' : 'Disabled' }}
					</template>
					<template #item.action="{ item }">
						<v-menu offset-y>
							<template #activator="{ on, attrs }">
								<v-btn
									icon
									v-bind="attrs"
									v-on="on"
									@click.stop
								>
									<v-icon>
										mdi-dots-horizontal
									</v-icon>
								</v-btn>
							</template>
							<v-list dense>
								<v-list-item
									v-if="userFullPermissions && ((item.brandId === 0 && userHasUniversalBrand) || item.brandId !== 0)"
									@click="$emit('edit-link', item, ACTION_LINK_TYPE)"
								>
									<v-list-item-content>
										<v-list-item-title>
											Edit
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-else-if="userReadOnly || (item.brandId === 0 && !userHasUniversalBrand)"
									@click="$emit('edit-link', item, ACTION_LINK_TYPE)"
								>
									<v-list-item-content>
										<v-list-item-title>
											View
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item @click.stop.prevent="$emit('view-link', item)">
									<v-list-item-content>
										<v-list-item-title>
											View Embed Code
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-if="shouldShowPreview(item)"
									@click.stop.prevent="$emit('preview-link', item)"
								>
									<v-list-item-content>
										<v-list-item-title>
											Preview Link
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-if="userFullPermissions && ((item.brandId === 0 && userHasUniversalBrand) || item.brandId !== 0)"
									@click.stop.prevent="$emit('clone-link', item, ACTION_LINK_TYPE)"
								>
									<v-list-item-content>
										<v-list-item-title>
											Clone
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-if="userFullPermissions && ((item.brandId === 0 && userHasUniversalBrand) || item.brandId !== 0)"
									@click.stop.prevent="$emit('remove-link', item)"
								>
									<v-list-item-content>
										<v-list-item-title>
											Delete
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</v-list>
						</v-menu>
					</template>
				</DataTable>
			</template>
		</SectionCard>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import FilterBar from '../../../../../../shared/components/filter-bar.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import SectionCard from '../../../../../../shared/components/section-card.vue'
import DataTable from '../../../../../../shared/components/data-table.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'

import { changeActionLinkSearchQuery, actionLinkSearchQuery, changeLinkAuthType, linkAuthType, changeLinkEnabledStatus, linkEnabledStatus } from '../../../../../../shared/state/links.js'
import { changeAdminPortalBrandFilter, selectedAdminPortalBrandId } from '../../../../../../shared/state/brands.js'
import { ACTION_LINK_TYPE } from '../link-types.js'
import { useLabels } from '../../../../../../shared/state/admin-portal-navigation.js'

export default {
	components: { SectionCard, DataTable, FilterBar, PrimaryActionButton, Dropdown },
	props: {
		actionLinks: {
			type: Array,
			default: () => []
		},
		linkAuthTypes: {
			type: Array,
			default: () => []
		},
		statusFilterItems: {
			type: Array,
			default: () => []
		},
		userFullPermissions: Boolean,
		userReadOnly: Boolean
	},
	setup () {
		return {
			changeActionLinkSearchQuery,
			actionLinkSearchQuery,
			changeLinkAuthType,
			linkAuthType,
			changeAdminPortalBrandFilter,
			selectedAdminPortalBrandId,
			changeLinkEnabledStatus,
			linkEnabledStatus
		}
	},
	data () {
		return {
			ACTION_LINK_TYPE
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission', 'userHasUniversalBrand']),
		authTypeFilter () {
			const authTypes = this.linkAuthTypes
			return [
				{ value: null, text: 'View All' }, ...authTypes.sort((a, b) => a.text.localeCompare(b.text))
			]
		},
		filteredActionLinks () {
			return this.actionLinks.filter(({ linkName, brandId, authenticationTypeId, isEnabled }) => {
				let check = true
				if (actionLinkSearchQuery.value) check = linkName.toLowerCase().includes(actionLinkSearchQuery.value.toLowerCase())
				if (selectedAdminPortalBrandId.value !== null) check = check && brandId === selectedAdminPortalBrandId.value
				if (linkAuthType.value !== null) check = check && authenticationTypeId === parseInt(linkAuthType.value)
				if (linkEnabledStatus.value !== null) check = check && isEnabled === linkEnabledStatus.value
				return check
			})
		},
		tableHeaders () {
			return [
				{ text: 'ID', value: 'linkId', width: '12%' },
				{ text: useLabels('Brand'), value: 'brandName', width: '18%' },
				{ text: 'Link Name', value: 'linkName', width: '25%' },
				{ text: 'Authentication Type', value: 'authenticationType', width: '20%' },
				{ text: 'Status', value: 'isEnabled', width: '12%' },
				{ text: 'Created Date', value: 'formattedCreatedDate', width: '13%' },
				{ text: 'Action', value: 'action', width: '80px', sortable: false, align: 'center' }
			]
		}
	},
	methods: {
		rowClick (row) {
			this.$emit('edit-link', row, ACTION_LINK_TYPE)
		},
		clearFilters () {
			changeLinkAuthType(null)
			changeLinkEnabledStatus(null)
		},
		shouldShowPreview (link) {
			return !link.isPreviewValueEmptyOrNull
		}
	}
}
</script>
